/**
 * HISIP-005: Profile Editor Page
 * 
 * Full-featured editor for Creative Profiles with stage grid, allowed styles,
 * and preset loader. Uses futuristic design system.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Settings, Palette, Zap, AlertTriangle } from 'lucide-react';
import { useProfile } from '@/hooks/useProfiles';
import { useGroupedTemplates } from '@/hooks/useTemplates';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import HolographicCard, { CardHeader, CardContent } from '@/components/futuristic/HolographicCard';
import StageSettingsGrid from '@/components/profiles/StageSettingsGrid';
import AllowedStylesPanel from '@/components/profiles/AllowedStylesPanel';
import PresetLoaderModal from '@/components/profiles/PresetLoaderModal';
import { cn } from '@/utils/cn';
import type { StageSettingsInput, ValidationError } from '@/hooks/useProfiles';
import { useDashboardServerStore } from '@/stores/dashboard-server';

const ProfileEditorPage: React.FC = () => {
  const { projectId, profileId } = useParams<{ projectId: string; profileId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useDashboardServerStore();
  
  const [showPresetLoader, setShowPresetLoader] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Profile data
  const {
    profile,
    stageSettings,
    allowedStyles,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
    updateProfile,
    replaceStageSettings,
    replaceAllowedStyles,
    loadPreset
  } = useProfile(profileId || null);

  // Templates data
  const {
    templates,
    loading: templatesLoading,
    error: templatesError
  } = useGroupedTemplates();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Stage settings state (7 stages)
  const [stageFormData, setStageFormData] = useState<Record<string, StageSettingsInput>>({});
  
  // Allowed styles state
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name,
        description: profile.description || ''
      });
    }
  }, [profile]);

  // Initialize stage settings when they load
  useEffect(() => {
    if (stageSettings.length > 0) {
      const stageData: Record<string, StageSettingsInput> = {};
      
      stageSettings.forEach(setting => {
        stageData[setting.stage] = {
          stage: setting.stage,
          writer_persona_id: setting.writer_persona_id,
          evaluator_persona_id: setting.evaluator_persona_id,
          rubric_id: setting.rubric_id,
          provider: setting.provider,
          model: setting.model,
          default_style_id: setting.default_style_id
        };
      });
      
      setStageFormData(stageData);
    }
  }, [stageSettings]);

  // Initialize allowed styles when they load
  useEffect(() => {
    if (allowedStyles.length > 0) {
      setSelectedStyles(allowedStyles.map(style => style.style_id));
    }
  }, [allowedStyles]);

  if (!projectId || !profileId) {
    return (
      <div className="void-foundation min-h-screen flex items-center justify-center">
        <div className="text-red-400 font-mono">Invalid profile ID</div>
      </div>
    );
  }

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleStageChange = (stage: string, field: string, value: string) => {
    setStageFormData(prev => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        stage: stage as any,
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleStylesChange = (styleSlugs: string[]) => {
    setSelectedStyles(styleSlugs);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setValidationErrors([]);
    
    try {
      // 1. Update profile info
      await updateProfile({
        name: formData.name,
        description: formData.description || null
      });

      // 2. Update stage settings
      const stages = Object.values(stageFormData).filter(stage => stage.stage);
      const stageResult = await replaceStageSettings(stages);
      
      if (!stageResult.success && stageResult.errors) {
        setValidationErrors(stageResult.errors);
        addNotification({ type: "error", title: 'Please fix validation errors', duration: 4000 });
        return;
      }

      // 3. Update allowed styles
      const stylesResult = await replaceAllowedStyles(selectedStyles);
      
      if (!stylesResult.success && stylesResult.errors) {
        setValidationErrors(stylesResult.errors);
        addNotification({ type: "error", title: 'Please fix validation errors', duration: 4000 });
        return;
      }

      setHasUnsavedChanges(false);
      addNotification({ type: "success", title: 'Profile saved successfully', duration: 3000 });
      
    } catch (error) {
      console.error('Error saving profile:', error);
      addNotification({ type: "error", title: 'Failed to save profile', duration: 4000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePresetLoad = async (presetKey: string) => {
    const result = await loadPreset(presetKey as any);
    if (result.success) {
      setShowPresetLoader(false);
      setHasUnsavedChanges(false);
      // Profile data will be refetched automatically
    }
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/settings/${projectId}/profiles`);
      }
    } else {
      navigate(`/settings/${projectId}/profiles`);
    }
  };

  // Loading state
  if (profileLoading || templatesLoading) {
    return (
      <div className="void-foundation min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="loading-spinner w-8 h-8 border-cyan-400"></div>
          <span className="text-cyan-400 font-mono">LOADING PROFILE...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (profileError || templatesError) {
    return (
      <div className="void-foundation min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 font-mono mb-4">
            ERROR: {profileError || templatesError}
          </div>
          <button 
            onClick={() => {
              refetchProfile();
              window.location.reload();
            }}
            className="btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
          >
            RETRY
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="void-foundation min-h-screen">
      <AtmosphericBackground />
      
      <div className="relative z-10 p-6 space-y-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackClick}
              className="p-2 text-white/40 hover:text-cyan-400 transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-4xl font-light text-gradient-cyan tracking-wider mb-2">
                {profile?.name || 'PROFILE EDITOR'}
              </h1>
              <p className="text-white/50 font-mono text-sm">
                CONFIGURE PERSONAS, STYLES & PIPELINE SETTINGS
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowPresetLoader(true)}
              className="btn-future text-purple-400 border-purple-400/50 hover:bg-purple-400/10 flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">LOAD PRESET</span>
            </button>
            
            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || isSaving}
              className={cn(
                "btn-future flex items-center gap-2",
                hasUnsavedChanges
                  ? "text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10"
                  : "text-white/30 border-white/10 cursor-not-allowed"
              )}
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">SAVING...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span className="hidden sm:inline">SAVE CHANGES</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <HolographicCard glow="error" intensity="subtle">
            <CardContent>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-red-400 font-mono text-sm mb-2">VALIDATION ERRORS</h3>
                  <ul className="space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index} className="text-white/70 text-sm">
                        <span className="text-red-400">{error.field}:</span> {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </HolographicCard>
        )}

        {/* Profile Info */}
        <HolographicCard glow="blue" intensity="subtle">
          <CardHeader 
            title="PROFILE INFORMATION" 
            subtitle="Basic profile settings and description"
            icon={<Settings className="w-5 h-5" />}
          />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                  Profile Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                           text-white/90 font-mono text-sm
                           focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                           transition-all"
                  maxLength={100}
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                           text-white/90 font-mono text-sm
                           focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                           transition-all resize-none"
                  rows={3}
                  maxLength={500}
                  placeholder="Injected into persona prompts..."
                />
                <p className="text-xs text-white/40 mt-1 font-mono">
                  Description will be injected into persona prompts
                </p>
              </div>
            </div>
          </CardContent>
        </HolographicCard>

        {/* Allowed Styles */}
        <HolographicCard glow="purple" intensity="subtle">
          <CardHeader 
            title="ALLOWED STYLES" 
            subtitle="Visual styles available for this profile"
            icon={<Palette className="w-5 h-5" />}
          />
          <CardContent>
            <AllowedStylesPanel
              availableStyles={templates.styles}
              selectedStyles={selectedStyles}
              onChange={handleStylesChange}
            />
          </CardContent>
        </HolographicCard>

        {/* Stage Settings Grid */}
        <HolographicCard glow="cyan" intensity="subtle">
          <CardHeader 
            title="STAGE CONFIGURATION" 
            subtitle="Configure personas, rubrics, and providers for each pipeline stage"
            icon={<Settings className="w-5 h-5" />}
          />
          <CardContent>
            <StageSettingsGrid
              stageSettings={stageFormData}
              templates={templates}
              onChange={handleStageChange}
            />
          </CardContent>
        </HolographicCard>
      </div>

      {/* Preset Loader Modal */}
      {showPresetLoader && (
        <PresetLoaderModal
          onLoadPreset={handlePresetLoad}
          onClose={() => setShowPresetLoader(false)}
        />
      )}
    </div>
  );
};

export default ProfileEditorPage;